import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { sortByCriteria } from "../../helpers/comparison";
import { useHorizontalScroll } from "../../hooks/useHorizontalScroll";
import theme from "../../theme";
import { CRMAppLocationIdentifier, FaqProps } from "../../types/Faq";
import RestrictedAppScope from "../RestrictedAppScope";
import CopyExternalUrlField from "../utils/CopyExternalUrlField";
import HorizontalScroller from "../utils/HorizontalScroller";
import APComparisonColumnHighlight from "./APComparisonColumnHighlight";
import APComparisonCriteriaHighlight from "./APComparisonCriteriaHighlight";
import APComparisonScrollNavigation from "./APComparisonScrollNavigation";
import APComparisonStickyHeader from "./APComparisonStickyHeader";
import APComparisonTable from "./APComparisonTable";

const Z_INDEX_COLUMN_HIGHTLIGHT = -1;
const Z_INDEX_TABLE = 1;
const Z_INDEX_LABELS = 10;
const Z_INDEX_OVERLAYS = 11;
const Z_INDEX_STICKY_HEADER = 12;

const DESKTOP_LABEL_WIDTH = 130;
const ITEM_WIDTH = 140;
const SMALL_ITEM_WIDTH = 110;
const LABEL_ITEM_WIDTH = 110;
const SMALL_LABEL_ITEM_WIDTH = 100;

export type ComparisonCriterion<T> = {
  label: string;
  labelEndAdornment?: ReactNode;
  labelAppLocationId?: CRMAppLocationIdentifier;
  sortable?: {
    direction: "asc" | "desc";
    function?: (a: T, b: T, direction: "asc" | "desc") => number;
    highlightWinner?: boolean;
  };
  styles?: {
    topAlign?: boolean;
    smallFont?: boolean;
  };
  valueRenderer?: (item: T) => ReactNode;
  labelRenderer?: () => ReactNode;
  valueFormatter?: (value: unknown) => ReactNode;
  getAppLocationId?: (item: T, index: number) => CRMAppLocationIdentifier;
};

export type ExpandableCriteria<T> = {
  id: string;
  label: string;
  criteria: (keyof T)[];
  expandByDefault: boolean;
};

export type Comparison<T> = {
  items: T[];
  criteria: Partial<Record<keyof T, ComparisonCriterion<T>>>;
  headerCriteria: keyof T;
  initialPrimaryCriteria: keyof T;
  selectedCriteria: (keyof T)[];
  alwaysShownTopCriteria?: (keyof T)[];
  alwaysShownBottomCriteria?: (keyof T)[];
  expandableCriteria: ExpandableCriteria<T>[];
  summaryCriteria?: keyof T;
  highlightWinner: boolean;
  faqProps?: FaqProps;
  onSort?: (criteriaId: keyof T) => void;
};

type APComparisonConfig = {
  widths: {
    item?: number;
    label?: number;
    mobileLabel?: number;
    mobileItem?: number;
    container?: number;
  };
};

interface APComparisonProps<T> {
  comparison: Comparison<T>;
  config?: APComparisonConfig;
  containerRef: RefObject<HTMLDivElement>;
}

const APComparison = <T extends Record<string, unknown>>({
  comparison,
  config,
  containerRef,
}: APComparisonProps<T>) => {
  const numberOfItems = comparison.items.length;
  const [containerWidth, setContainerWidth] = useState(0);
  const calculatedItemWidth = containerWidth
    ? (containerWidth - DESKTOP_LABEL_WIDTH) / numberOfItems
    : ITEM_WIDTH;

  const widths = {
    item: config?.widths?.item
      ? config.widths.item
      : Math.max(calculatedItemWidth, ITEM_WIDTH),
    label: config?.widths?.label ?? DESKTOP_LABEL_WIDTH,

    mobileItem: config?.widths?.mobileItem ?? ITEM_WIDTH,
    mobileLabel: config?.widths?.mobileLabel ?? LABEL_ITEM_WIDTH,

    smallMobileItem: SMALL_ITEM_WIDTH,
    smallMobileLabel: SMALL_LABEL_ITEM_WIDTH,
  };

  const mobileBreakpoint = useMediaQuery(theme.breakpoints.down("sm"));
  const smallMobileBreakpoint = useMediaQuery(theme.breakpoints.down("xsm"));

  let itemWidth = widths.item;
  let labelItemWidth = widths.label;

  if (mobileBreakpoint) {
    itemWidth = widths.mobileItem;
    labelItemWidth = widths.mobileLabel;
  }
  if (smallMobileBreakpoint) {
    itemWidth = widths.smallMobileItem;
    labelItemWidth = widths.smallMobileLabel;
  }

  const { scrollRef, scrollLeft, scrollRight, resetScroll, scrollState } =
    useHorizontalScroll(itemWidth);
  const headerRowRef = useRef<HTMLTableRowElement>(null);
  const labelRefs = useRef<Record<string, HTMLElement>>({});
  const [expandedCriteria, setExpandedCriteria] = useState<
    Record<string, boolean>
  >(
    Object.fromEntries(
      comparison.expandableCriteria.map((criteria) => [
        criteria.id,
        criteria.expandByDefault,
      ])
    )
  );
  const [sortCriteria, setSortCriteria] = useState<keyof T>(
    comparison.initialPrimaryCriteria
  );

  const onSortItems = (items: T[], criteriaId: keyof T) => {
    const criteria = comparison.criteria[criteriaId];
    const sortedItems = criteria
      ? sortByCriteria(items, criteriaId, criteria)
      : items;
    const maxValue = sortedItems[0][criteriaId];
    const allEqualValue = sortedItems.every(
      (item) => item[criteriaId] === maxValue
    );

    return { maxValue, allEqualValue, sortedItems };
  };

  const sortedItems = useMemo(() => {
    const bySortCriteria = onSortItems(comparison.items, sortCriteria);

    if (bySortCriteria.allEqualValue) {
      const byInitialSortCriteria = onSortItems(
        comparison.items,
        comparison.initialPrimaryCriteria
      );
      return byInitialSortCriteria.sortedItems;
    }

    return bySortCriteria.sortedItems;
  }, [comparison.items, comparison.criteria, sortCriteria]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  const onToggleExpandedCriteria = (expandableCriteriaId: string) => {
    setExpandedCriteria((prev) => ({
      ...prev,
      [expandableCriteriaId]: !prev[expandableCriteriaId],
    }));
  };

  const onSetSortCriteria = (criteriaId: keyof T) => {
    setSortCriteria(criteriaId);

    // Call the onSort function if it exists
    comparison.onSort?.(criteriaId);
  };

  const renderScrollNavigation = () => {
    const showLeft = scrollState === "between" || scrollState === "end";
    const showRight = scrollState === "between" || scrollState === "start";

    return (
      <Box
        sx={{
          position: "absolute",
          left: labelItemWidth - 24,
          right: -16,
          top: 0,
          zIndex: Z_INDEX_OVERLAYS,
          pointerEvents: "none",
        }}
      >
        <APComparisonScrollNavigation
          showLeft={showLeft}
          showRight={showRight}
          onScrollLeft={scrollLeft}
          onScrollRight={scrollRight}
        />
      </Box>
    );
  };

  const renderColumnHighlights = () => {
    const leftOffsets = [];
    const numHighlights = Math.ceil(sortedItems.length / 2);

    for (let i = 0; i < numHighlights; i++) {
      leftOffsets.push(i * 2 * itemWidth + labelItemWidth);
    }

    return leftOffsets.map((leftOffset, index) => (
      <APComparisonColumnHighlight
        key={`${leftOffset}-column-highlight`}
        color={
          index === 0 && comparison.highlightWinner
            ? "tertiaryGreen.main"
            : "neutralGray.light"
        }
        leftOffset={leftOffset}
        itemWidth={itemWidth}
        zIndex={Z_INDEX_COLUMN_HIGHTLIGHT}
      />
    ));
  };

  return (
    <Stack spacing={2}>
      <Box>
        <RestrictedAppScope scope="internal">
          <CopyExternalUrlField text="Copy the URL below to share this comparison (this is a driver specific url):" />
        </RestrictedAppScope>
      </Box>
      <Box>
        <Typography variant="labelLarge">
          Click on criteria to sort list
        </Typography>
      </Box>
      {containerWidth && (
        <Box sx={{ position: "relative" }}>
          {comparison.headerCriteria && (
            <APComparisonStickyHeader
              comparison={comparison}
              sortedItems={sortedItems}
              scrollRef={scrollRef}
              headerRowRef={headerRowRef}
              scrollNavigation={renderScrollNavigation()}
              itemWidth={itemWidth}
              labelItemWidth={labelItemWidth}
              zIndex={Z_INDEX_STICKY_HEADER}
            />
          )}
          <Box sx={{ position: "relative" }}>
            <HorizontalScroller
              scrollRef={scrollRef}
              itemWidth={itemWidth}
              numberOfItems={comparison.items.length}
              containerOffset={labelItemWidth}
              overlayElement={
                <>
                  {renderScrollNavigation()}
                  <APComparisonCriteriaHighlight
                    comparison={comparison}
                    expandedCriteria={expandedCriteria}
                    sortCriteria={sortCriteria}
                    resetScroll={resetScroll}
                    labelRefs={labelRefs}
                    scrollRef={scrollRef}
                    zIndex={Z_INDEX_OVERLAYS}
                  />
                </>
              }
            >
              <Box sx={{ position: "relative", zIndex: Z_INDEX_TABLE }}>
                {renderColumnHighlights()}
                <APComparisonTable
                  comparison={comparison}
                  sortedItems={sortedItems}
                  itemSorter={onSortItems}
                  expandedCriteria={expandedCriteria}
                  itemWidth={itemWidth}
                  labelItemWidth={labelItemWidth}
                  labelRefs={labelRefs}
                  headerRowRef={headerRowRef}
                  zIndex={Z_INDEX_LABELS}
                  onSetSortCriteria={onSetSortCriteria}
                  onToggleExpandedCriteria={onToggleExpandedCriteria}
                />
              </Box>
            </HorizontalScroller>
            <Box
              sx={{
                position: "absolute",
                bottom: -1,
                left: 0,
                height: "16px",
                width: `${labelItemWidth}px`,
                backgroundColor: "white",
                zIndex: Z_INDEX_OVERLAYS + 1,
              }}
            />
          </Box>
        </Box>
      )}
    </Stack>
  );
};

export default APComparison;
